import React, { useEffect } from 'react';
import { ListColumn } from '../Datalist/Datalist';
import Datalist from '../Datalist/Datalist';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import './FollowUpSendingTable.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { statusIconColorMap } from '../../lib/utils';
import { faExclamationCircle, faCheckCircle, faTimesCircle, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';
import { faCommentDots } from "@fortawesome/free-solid-svg-icons";
import InternalCommentsModal from "../InternalComments/InternalCommentsModal";

interface Sending {
    id: number;
    customerName: string;
    orgNumber: string;
    createdDate: string;
    updatedDate: string;
    status: number;
    isHandled: boolean;
    supplierName: string;
    isSelected?: boolean; // New field for selection state
    hasInternalComment?: boolean; // NY
}

interface FollowUpSendingTableProps {
    searchText: string;
    orgNumber: string;
    name: string;
    status: string;
    isHandled: string; // Filter for handled/unhandled
    isLoading: boolean;
    sendingData: Sending[];
    refetchData: () => void; // Function to refetch data
    onSelectSending: (sendingId: number, isSelected: boolean) => void; // New prop to handle selection
    selectedSendingIds: number[]; // New prop for selected rows
}

const FollowUpSendingTable: React.FC<FollowUpSendingTableProps> = ({
    searchText,
    orgNumber,
    name,
    status,
    isHandled,
    isLoading,
    sendingData,
    refetchData,
    onSelectSending,
    selectedSendingIds,
}) => {
    const [showCommentsModal, setShowCommentsModal] = React.useState(false);
    const [commentsTargetSendingId, setCommentsTargetSendingId] = React.useState<number | null>(null);
    const openComments = (sendingId: number) => {
        setCommentsTargetSendingId(sendingId);
        setShowCommentsModal(true);
    };

    const navigate = useNavigate();

    // Filter sending data based on search text, orgNumber, name, status, and isHandled
    // const filteredData = sendingData.filter((sending) => {
    //     const matchesSearch =
    //         sending.customerName?.toLowerCase().includes(searchText.toLowerCase()) ||
    //         sending.orgNumber?.includes(searchText) ||
    //         sending.id.toString().includes(searchText) ||
    //         String(sending.status).includes(searchText) ||
    //         sending.supplierName.toLowerCase().includes(searchText.toLowerCase());

    //     const matchesOrgNumber = !orgNumber || sending.orgNumber.includes(orgNumber);
    //     const matchesName = !name || sending.customerName.toLowerCase().includes(name.toLowerCase());
    //     const matchesStatus = !status || String(sending.status) === status;

    //     // Filter for isHandled based on filter value
    //     const matchesIsHandled =
    //         isHandled === 'all' ||
    //         (isHandled === 'handled' && sending.isHandled) ||
    //         (isHandled === 'unhandled' && !sending.isHandled);

    //     return matchesSearch && matchesOrgNumber && matchesName && matchesStatus && matchesIsHandled;
    // });
    const filteredData = sendingData.filter((sending) => {
  const customerName = (sending.customerName ?? "").toLowerCase();
  const supplierName = (sending.supplierName ?? "").toLowerCase();
  const search = (searchText ?? "").toLowerCase();
  const nameFilter = (name ?? "").toLowerCase();

  const matchesSearch =
    customerName.includes(search) ||
    (sending.orgNumber ?? "").includes(searchText) ||
    sending.id.toString().includes(searchText) ||
    String(sending.status).includes(searchText) ||
    supplierName.includes(search);

  const matchesOrgNumber = !orgNumber || (sending.orgNumber ?? "").includes(orgNumber);
  const matchesName = !name || customerName.includes(nameFilter);
  const matchesStatus = !status || String(sending.status) === status;

  const matchesIsHandled =
    isHandled === "all" ||
    (isHandled === "handled" && !!sending.isHandled) ||
    (isHandled === "unhandled" && !sending.isHandled);

  return matchesSearch && matchesOrgNumber && matchesName && matchesStatus && matchesIsHandled;
});

    // Define columns for the sending table
    const columns: ListColumn<Sending>[] = [
        {
            key: 'select',
            fieldName: 'select',
            name: '',
            maxWidth: 50,
            minWidth: 50,
            isActive: true,
            onRender: (item: Sending) => (
                <span
                    onClick={(ev) => {
                        ev.stopPropagation();
                        return !item.isHandled && onSelectSending(item.id, !selectedSendingIds.includes(item.id))
                    }
                    }
                    style={{ cursor: item.isHandled ? 'not-allowed' : 'pointer', opacity: item.isHandled ? 0.5 : 1 }}
                >
                    {item.isHandled ? (
                        <FontAwesomeIcon icon={faSquare} className="custom-checkbox" />
                    ) : selectedSendingIds.includes(item.id) ? (
                        <FontAwesomeIcon icon={faCheckSquare} className="custom-checkbox checked" />
                    ) : (
                        <FontAwesomeIcon icon={faSquare} className="custom-checkbox" />
                    )}
                </span>
            ),
        },
        {
            key: 'id',
            fieldName: 'id',
            name: 'Id',
            maxWidth: '10%',
            minWidth: 50,
            isActive: true,
            sorting: true,
            onRender: (item: Sending) => item.id.toString(),
            footer: (items: Sending[]) => `Totalt antal rader: ${items.length}`,
        },
        {
  key: "internalComments",
  fieldName: "internalComments",
  name: "",
  maxWidth: 50,
  minWidth: 50,
  isActive: true,
  sorting: false,
  onRender: (item: Sending) => (
    <span
      onClick={(ev) => {
        ev.stopPropagation();
        openComments(item.id);
      }}
      style={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
      title="Interna kommentarer"
    >
      <FontAwesomeIcon
        icon={faCommentDots}
        style={{ color: item.hasInternalComment ? "#0d6efd" : "#adb5bd" }}
      />
    </span>
  ),
},

        {
            key: 'organisation',
            fieldName: 'organisation',
            name: 'Medlem',
            maxWidth: '30%',
            minWidth: 100,
            isActive: true,
            sorting: true,
            // onRender: (item: Sending) => (`${item.customerName} - ${item.orgNumber}`),
            onRender: (item: Sending) => (`${item.customerName ?? "Okänd kund"} - ${item.orgNumber}`),
        },
        {
            key: 'supplierName',
            fieldName: 'supplierName',
            name: 'Leverantör',
            maxWidth: '20%',
            minWidth: 100,
            isActive: true,
            sorting: true,
            onRender: (item: Sending) => item.supplierName,
        },
        {
            key: 'createdDate',
            fieldName: 'createdDate',
            name: 'Datum',
            maxWidth: '20%',
            minWidth: 100,
            isActive: true,
            sorting: true,
            onRender: (item: Sending) => {
                const parsedDate = new Date(item.createdDate);
                return isNaN(parsedDate.getTime()) ? 'Invalid Date' : format(parsedDate, 'yyyy-MM-dd');
            },
        },
        {
            key: 'updatedDate',
            fieldName: 'updatedDate',
            name: 'Uppdaterad',
            maxWidth: '20%',
            minWidth: 100,
            isActive: true,
            sorting: true,
            onRender: (item: Sending) => {
                const parsedDate = new Date(item.updatedDate);
                return isNaN(parsedDate.getTime()) ? 'Invalid Date' : format(parsedDate, 'yyyy-MM-dd');
            },
        },
        {
            key: 'status',
            fieldName: 'status',
            name: 'Status',
            maxWidth: '20%',
            minWidth: 50,
            isActive: true,
            sorting: true,
            onRender: (item: Sending) => {
                const statusInfo = statusIconColorMap[item.status] || { label: 'Unknown', color: '#000', icon: faExclamationCircle };
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FontAwesomeIcon icon={statusInfo.icon} style={{ marginRight: 8, color: statusInfo.color }} />
                        <span>{statusInfo.label}</span>
                    </div>
                );
            },
        },
        {
            key: 'isHandled',
            fieldName: 'isHandled',
            name: 'Slutförd',
            maxWidth: '10%',
            minWidth: 50,
            isActive: true,
            sorting: true,
            onRender: (item: Sending) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FontAwesomeIcon
                        icon={item.isHandled ? faCheckCircle : faTimesCircle}
                        style={{ marginRight: 8, color: item.isHandled ? '#07A235' : '#DC143C' }}
                    />
                    <span>{item.isHandled ? 'Ja' : 'Nej'}</span>
                </div>
            ),
        },
    ];

    return (
        <div className="follow-up-sending-table-wrapper">
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                <Datalist
                    items={filteredData}
                    isLoading={isLoading}
                    columns={columns}
                    onClick={(item) => {navigate(`/followup-sending/${item.id}`)}}
                    setColumns={() => { }}
                    initialSortKey="updatedDate"
                    descendingSort={false}
                    noItemsText="Inga sändningar att visa"
                    uniqueKey="id"
                />
            </div>
            {commentsTargetSendingId !== null && (
  <InternalCommentsModal
    show={showCommentsModal}
    onClose={() => setShowCommentsModal(false)}
    targetType="sending"
    targetId={commentsTargetSendingId}
    onCreated={refetchData} // så ikon/badge uppdateras direkt efter ny kommentar
  />
)}

        </div>
    );
};

export default FollowUpSendingTable;
